// packages import
import { Link, useSearchParams } from 'react-router-dom'
import { FC, useContext, useState } from 'react'
import {
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  Chip,
  Checkbox,
  IconButton
} from '@mui/material'
import MoreVertIcon from '@mui/icons-material/MoreVert'

// component import
import { CustomPagination } from '../../common/CustomPagination'
import { StickyTable } from '../../common/StickyTable'
import { TableSkeleton } from '../../skeletons/TableSkeleton'
import { FundRequestMenu } from '../../menus/FundRequest'

// others
import { FundRequestsListingProps } from '../../../interfaceTypes'
import { formatCurrency } from '../../../utils'
import { formatStatus, statusColors } from '../../../utils/billing'
import { QUERY_PARAMS } from '../../../constants'
import { AuthContext } from '../../../context'
import { BillStatus } from '../../../../generated'
import palette from '../../../theme/palette'

export const FundRequestsListing: FC<FundRequestsListingProps> = ({ fundRequests, loading, refetch }): JSX.Element => {
  const { isAdmin, isSuperAdmin, isAccountant } = useContext(AuthContext)
  const [selectedItems, setSelectedItems] = useState<string[]>([])
  const [selectAll, setSelectAll] = useState(false)

  const [searchParams, setSearchParams] = useSearchParams()

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked
    setSelectAll(checked)
    if (checked) {
      searchParams.set(QUERY_PARAMS.SELECTED_BILLS, QUERY_PARAMS.ALL)
      setSelectedItems(fundRequests?.map(fundRequest => String(fundRequest.id)) || [])
    } else {
      searchParams.delete(QUERY_PARAMS.SELECTED_BILLS)
      setSelectedItems([])
    }
    setSearchParams(searchParams)
  }

  const handleRowSelect = (billId: string) => {
    const isSelected = selectedItems.includes(billId)
    if (isSelected) {
      const selectedId = selectedItems.filter(id => id !== billId)

      if (!selectedId.length) searchParams.delete(QUERY_PARAMS.SELECTED_BILLS)
      else searchParams.set(QUERY_PARAMS.SELECTED_BILLS, selectedId.join(','))

      setSelectedItems(selectedId)
      setSelectAll(false)
    } else {
      const newSelection = [...selectedItems, billId]
      setSelectedItems(newSelection)
      if (newSelection.length === fundRequests?.length) {
        setSelectAll(true)
        searchParams.set(QUERY_PARAMS.SELECTED_BILLS, QUERY_PARAMS.ALL)
      } else searchParams.set(QUERY_PARAMS.SELECTED_BILLS, newSelection.join(','))
    }
    setSearchParams(searchParams)
  }

  const showCheckBox = isAccountant || isAdmin || isSuperAdmin

  return (
    <StickyTable pagination={<CustomPagination loading={loading || false} setParams={true} />} height="400px">
      <TableHead>
        <TableRow>
          {showCheckBox && (
            <TableCell width="5%">
              <Checkbox checked={selectAll} onChange={handleSelectAll} size="small" />
            </TableCell>
          )}
          <TableCell width={showCheckBox ? '8%' : '6%'}> ID </TableCell>
          <TableCell width="35%"> Matter </TableCell>
          <TableCell width="20%"> Matter # </TableCell>
          <TableCell width="20%"> Amount </TableCell>
          <TableCell width="20%"> Status </TableCell>
          <TableCell width="5%"> Action </TableCell>
        </TableRow>
      </TableHead>

      {loading ? (
        <TableSkeleton noOfRows={7} noOfCols={showCheckBox ? 6 : 5} />
      ) : (
        <TableBody>
          {fundRequests?.map(fundRequest => {
            const { id, case: caseData, amount, status, sendToClient } = fundRequest || {}

            const { caseNumber, consultMatterNumber, title } = caseData || {}

            const formattedStatus =
              status === BillStatus.Billed && sendToClient ? 'Ready to send' : formatStatus(status)
            const isSelected = selectedItems.includes(String(id))

            return (
              <TableRow key={id} hover selected={isSelected}>
                {showCheckBox && (
                  <TableCell>
                    <Checkbox checked={isSelected} onChange={() => handleRowSelect(String(id))} size="small" />
                  </TableCell>
                )}

                <TableCell>
                  <Link
                    to={`/billing/fund-request-detail/${id}`}
                    style={{
                      color: palette.primary.main,
                      textDecoration: 'none'
                    }}
                  >
                    {id}
                  </Link>
                </TableCell>

                <TableCell>
                  <Tooltip title={title || ''} arrow placement="top">
                    <Typography
                      noWrap
                      sx={{
                        textOverflow: 'ellipsis',
                        overflow: 'hidden',
                        maxWidth: '350px'
                      }}
                    >
                      {title || ''}
                    </Typography>
                  </Tooltip>
                </TableCell>

                <TableCell>
                  {!!(caseNumber || consultMatterNumber) && (
                    <Link
                      to={`/matters/matter-detail/${caseData?.id}`}
                      style={{
                        color: palette.primary.main,
                        textDecoration: 'none'
                      }}
                    >
                      #{caseNumber || consultMatterNumber}
                    </Link>
                  )}
                </TableCell>

                <TableCell>
                  <Typography>{formatCurrency(amount)}</Typography>
                </TableCell>

                <TableCell>
                  <Chip
                    label={formattedStatus}
                    sx={{
                      color: statusColors[status],
                      border: `1px solid ${statusColors[status]}50`,
                      backgroundColor: 'transparent'
                    }}
                  />
                </TableCell>

                <TableCell align="right">
                  <FundRequestMenu
                    caseId={caseData?.id}
                    bill={fundRequest}
                    billingType={fundRequest.case?.billing?.billingType}
                    onRefetch={refetch}
                    renderAction={handleClick => (
                      <IconButton onClick={handleClick}>
                        <MoreVertIcon />
                      </IconButton>
                    )}
                  />
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      )}
    </StickyTable>
  )
}
